/**
 * ModernPaymentDialog.tsx
 * Modern payment dialog with BIDV QR code payment integration
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@vsat/shared/components';
import { Button } from '@vsat/shared/components';
import { Badge } from '@vsat/shared/components';
import {
  Copy,
  CheckCircle2,
  Clock,
  Building2,
  User,
  Hash,
  Smartphone,
  Shield,
  ArrowRight,
  CheckCircle,
  Loader2,
  AlertCircle,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { apiService } from '@vsat/shared/lib/api';
import { AccessCodeInput, ValidatedCode } from '@vsat/shared/components';
import { BidvPromoBanner } from '@vsat/shared/components';

interface ModernPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  examTitle?: string;
  examTitleVi?: string;
  price: number;
  examId?: string;
  assessmentId?: string;
  selectedLevel?: 'THPT' | 'THCS';
  onSelectLevel?: (level: 'THPT' | 'THCS') => Promise<void>;
  onPaymentSuccess: (transactionId?: string) => void;
}

type PaymentStep = 'info' | 'processing' | 'free' | 'success';

export function ModernPaymentDialog({
  open,
  onOpenChange,
  examTitle = '',
  examTitleVi = '',
  price,
  examId,
  assessmentId,
  selectedLevel,
  onSelectLevel,
  onPaymentSuccess,
}: ModernPaymentDialogProps) {
  const { i18n } = useTranslation();
  const isVietnamese = true; // THPT app always uses Vietnamese
  const [isMockAllowed, setIsMockAllowed] = useState(false);

  useEffect(() => {
    const checkMockConfig = async () => {
      try {
        const config = await apiService.getConfigByKey('allowMockPayments');
        setIsMockAllowed(config?.value === 'true');
      } catch (err) {
        console.warn('Failed to check mock payment config:', err);
      }
    };
    checkMockConfig();
  }, []);
  const [step, setStep] = useState<PaymentStep>('info');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transactionId, setTransactionId] = useState('');
  const [_orderId, setOrderId] = useState<string | null>(null);
  const [qrData, setQrData] = useState<string | null>(null);
  const [qrCodeImage, setQrCodeImage] = useState<string | null>(null);
  const [accountNo, setAccountNo] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string | null>(null);
  const [bankName, setBankName] = useState<string | null>(null);
  const [_expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [paymentAmount, setPaymentAmount] = useState<number>(price);
  const [originalAmount, setOriginalAmount] = useState<number>(price);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedSuccessRef = useRef(false);
  const paymentSuccessCalledRef = useRef(false); // Prevent duplicate onPaymentSuccess calls
  const [_pollingAttempts, setPollingAttempts] = useState(0);
  const [isManualChecking, setIsManualChecking] = useState(false);
  const MAX_POLLING_ATTEMPTS = 60; // 5 minutes (60 * 5 seconds)
  const [validatedCode, setValidatedCode] = useState<ValidatedCode | null>(null);

  const formattedPrice = new Intl.NumberFormat('vi-VN').format(paymentAmount);

  const transactionRef = transactionId || '';

  // Create payment when dialog opens - Auto-generate QR immediately
  useEffect(() => {
    if (open && !transactionId && !isLoading) {
      createPaymentSession();
    }

    // Cleanup polling on unmount or when dialog closes
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [open]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setError(null);
      setIsPolling(false);
      setIsManualChecking(false);
      setPollingAttempts(0);
      // Reset state for next time
      setTransactionId('');
      setOrderId(null);
      setQrData(null);
      setQrCodeImage(null);
      setAccountNo(null);
      setAccountName(null);
      setBankName(null);
      setExpiresAt(null);
      setPaymentAmount(price);
      setOriginalAmount(price);
      setDiscountAmount(0);
      setStep('info');
      setCountdown(5);
      hasTrackedSuccessRef.current = false;
      paymentSuccessCalledRef.current = false; // Reset payment success flag
      setValidatedCode(null); // Reset validated code
    }
  }, [open]);

  const createPaymentSession = async (overrideCode?: string | null, overrideAmount?: number) => {
    // Stop existing polling if any
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);

    // Clear previous transaction state
    setTransactionId('');
    setOrderId(null);
    setQrData(null);
    setQrCodeImage(null);

    setIsLoading(true);
    setError(null);
    setStep('info');

    // Use override code if provided (to avoid race conditions with state updates)
    // Always send original price - backend will calculate discount based on accessCode
    const codeToUse = overrideCode !== undefined ? overrideCode : validatedCode?.code;

    // Determine amount to send. If overrideAmount provided (from AccessCodeInput), use it.
    // Otherwise backup to price.
    // NOTE: Backend expects exact amount match. If 100% discount, amount should be 0.
    const amountToUse =
      overrideAmount !== undefined ? overrideAmount : (validatedCode?.finalAmount ?? price);

    try {
      // Ensure amount is an integer
      const amount = Math.floor(Number(amountToUse));

      // Validation: Minimum 1000 VND, UNLESS it is 0 (free/100% discount)
      const isFree = amount === 0;
      if (isNaN(amount) || (amount < 1000 && !isFree)) {
        throw new Error('Số tiền không hợp lệ. Số tiền tối thiểu là 1,000 VNĐ.');
      }

      // Re-check mock config to ensure we have the latest value (avoids race conditions)
      let currentMockStatus = isMockAllowed;
      try {
        const config = await apiService.getConfigByKey('allowMockPayments');
        if (config) {
          currentMockStatus = config.value === 'true';
          setIsMockAllowed(currentMockStatus);
        }
      } catch (e) {
        console.warn('Failed to re-check mock status', e);
      }

      console.log('Creating payment with mock status:', currentMockStatus);

      const response = await apiService.createPayment({
        amount: amountToUse, // Send correct amount (0 for free, or original)
        examId,
        assessmentId,
        description: `Thanh toán bài thi: ${examTitleVi || examTitle}`,
        orderPrefix: 'CAT',
        isMock: currentMockStatus,
        accessCode: codeToUse || undefined, // Backend validates and applies discount
        sourceApp: 'THPT', // Track payment source from THPT app
      });

      // Handle Immediate Completion (Mock Payment or Free)
      if (response.status === 'COMPLETED') {
        setTransactionId(response.transactionId);
        setOrderId(response.orderId);
        setPaymentAmount(response.amount ?? 0);
        // Show free access confirmation screen instead of auto-completing
        setStep('free');
        toast.success(
          isVietnamese
            ? '🎉 Miễn phí toàn bộ! Bạn không cần thanh toán.'
            : '🎉 100% Free! No payment required.'
        );
        return;
      }

      setTransactionId(response.transactionId);
      setOrderId(response.orderId || null);
      setPaymentAmount(response.amount ?? price);
      setOriginalAmount(response.originalAmount ?? price);
      setDiscountAmount(response.discountAmount ?? 0);
      setQrData(response.qrData || null);
      setQrCodeImage(response.qrCode || null);
      setAccountNo(response.accountNo || null);
      setAccountName(response.accountName || null);
      setBankName(response.bankName || null);
      if (response.expiresAt) {
        setExpiresAt(new Date(response.expiresAt));
      }

      // Start polling for payment status
      startPolling(response.transactionId);

      toast.success(
        isVietnamese
          ? 'Mã QR thanh toán đã được tạo. Vui lòng quét mã để thanh toán.'
          : 'QR code created. Please scan to pay.'
      );
    } catch (err: any) {
      console.error('Failed to create payment:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });

      // Extract error message from various possible formats
      let errorMessage = isVietnamese
        ? 'Không thể tạo giao dịch thanh toán'
        : 'Failed to create payment';

      if (err.response?.data) {
        // Try different error message formats
        if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        } else if (Array.isArray(err.response.data.message)) {
          // Validation errors are often arrays
          errorMessage = err.response.data.message.join(', ');
        } else if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const startPolling = (txnId: string) => {
    if (isMockAllowed && !txnId) {
      return;
    }
    setIsPolling(true);
    setPollingAttempts(0);

    // Poll every 5 seconds
    pollingIntervalRef.current = setInterval(async () => {
      try {
        setPollingAttempts(prev => {
          const newAttempts = prev + 1;

          // Stop polling after max attempts (5 minutes)
          if (newAttempts >= MAX_POLLING_ATTEMPTS) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            setIsPolling(false);
            setError(
              isVietnamese
                ? 'Hết thời gian chờ. Nhấn "Kiểm tra ngay" hoặc tạo mã mới.'
                : 'Timeout. Tap "Check Now" or create a new code.'
            );
            return newAttempts;
          }

          return newAttempts;
        });

        const statusResponse = await apiService.getPaymentStatus(txnId);
        const status = statusResponse.status;

        if (status === 'COMPLETED') {
          // Payment successful
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsPolling(false);
          setStep('success');
          toast.success(isVietnamese ? 'Thanh toán thành công!' : 'Payment successful!');
          // onPaymentSuccess will be called after countdown
        } else if (status === 'FAILED' || status === 'EXPIRED' || status === 'REFUNDED') {
          // Payment failed or expired
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setIsPolling(false);
          const errorMsg =
            status === 'EXPIRED'
              ? isVietnamese
                ? 'Mã QR đã hết hạn. Vui lòng tạo mã thanh toán mới.'
                : 'QR code expired. Please create a new payment.'
              : status === 'REFUNDED'
                ? isVietnamese
                  ? 'Giao dịch đã được hoàn tiền.'
                  : 'Transaction refunded.'
                : isVietnamese
                  ? 'Giao dịch thanh toán đã thất bại.'
                  : 'Payment failed.';
          setError(errorMsg);
          toast.error(errorMsg);
        }
        // Continue polling if status is PENDING or PROCESSING
      } catch (err) {
        console.error('Error polling payment status:', err);
        // Continue polling on error (might be temporary)
      }
    }, 5000);
  };

  // Countdown for success screen
  useEffect(() => {
    if (step === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === 'success' && countdown === 0 && !paymentSuccessCalledRef.current) {
      paymentSuccessCalledRef.current = true; // Mark as called to prevent duplicate
      onPaymentSuccess(transactionId);
      onOpenChange(false);
    }
  }, [step, countdown, onPaymentSuccess, onOpenChange, transactionId]);

  useEffect(() => {
    if (step === 'success' && !hasTrackedSuccessRef.current) {
      hasTrackedSuccessRef.current = true;
      const gaPayload = {
        event: 'payment_success',
        examId: examId || null,
        transactionId,
        value: paymentAmount,
        currency: 'VND',
      };

      if (typeof window !== 'undefined') {
        const win = window as typeof window & {
          dataLayer?: Record<string, unknown>[];
          gtag?: (...args: any[]) => void;
        };

        win.dataLayer?.push(gaPayload);
        win.gtag?.('event', 'payment_success', {
          transaction_id: transactionId,
          value: paymentAmount,
          currency: 'VND',
          exam_id: examId,
        });
      }
    }
  }, [step, examId, paymentAmount, transactionId]);

  const handleCopy = async (text: string, field: string) => {
    if (!text) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success(isVietnamese ? 'Đã sao chép!' : 'Copied!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error(isVietnamese ? 'Không thể sao chép' : 'Failed to copy');
    }
  };

  const handleConfirmPayment = async () => {
    // Manual refresh/check button - useful for users who want to check immediately
    if (!transactionId) {
      toast.info(isVietnamese ? 'Vui lòng đợi mã QR được tạo...' : 'Please wait for QR code...');
      return;
    }

    setIsManualChecking(true);

    try {
      const statusResponse = await apiService.getPaymentStatus(transactionId);
      const status = statusResponse.status;

      if (status === 'COMPLETED') {
        // Payment successful - stop polling and show success
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setIsPolling(false);
        setStep('success');
        toast.success(isVietnamese ? 'Thanh toán thành công!' : 'Payment successful!');
      } else if (status === 'FAILED' || status === 'EXPIRED' || status === 'REFUNDED') {
        // Payment failed or expired
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setIsPolling(false);
        const errorMsg =
          status === 'EXPIRED'
            ? isVietnamese
              ? 'Mã QR đã hết hạn. Vui lòng tạo mã thanh toán mới.'
              : 'QR code expired. Please create a new payment.'
            : status === 'REFUNDED'
              ? isVietnamese
                ? 'Giao dịch đã được hoàn tiền.'
                : 'Transaction refunded.'
              : isVietnamese
                ? 'Giao dịch thanh toán đã thất bại.'
                : 'Payment failed.';
        setError(errorMsg);
        toast.error(errorMsg);
      } else {
        // Still pending/processing
        toast.info(
          isVietnamese
            ? 'Thanh toán đang được xử lý. Hệ thống sẽ tiếp tục kiểm tra tự động...'
            : 'Payment is being processed. System will continue checking automatically...'
        );

        // Restart polling if it stopped
        if (!isPolling && (status === 'PENDING' || status === 'PROCESSING')) {
          startPolling(transactionId);
        }
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      toast.error(
        isVietnamese
          ? 'Không thể kiểm tra trạng thái thanh toán. Vui lòng thử lại sau.'
          : 'Unable to check payment status. Please try again later.'
      );
    } finally {
      setIsManualChecking(false);
    }
  };

  const _checkPaymentStatusOnce = async () => {
    if (!transactionId) return;

    try {
      const statusResponse = await apiService.getPaymentStatus(transactionId);
      if (statusResponse.status === 'COMPLETED') {
        setStep('success');
        setIsPolling(false);
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } else {
        toast.info(
          isVietnamese
            ? 'Thanh toán chưa được xác nhận. Vui lòng đợi...'
            : 'Payment not confirmed yet. Please wait...'
        );
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  };

  const InfoField = ({ icon: Icon, label, value, copyable = false, fieldName = '' }: any) => (
    <div className='group relative rounded-lg border border-border bg-white p-4 transition-all hover:border-primary/30 hover:shadow-lg hover:ring-2 hover:ring-primary/20'>
      <div className='flex items-start gap-3'>
        <div className='flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary'>
          <Icon className='h-5 w-5' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='text-xs font-medium text-muted-foreground mb-1'>{label}</p>
          <p className='text-sm font-semibold text-foreground break-words'>{value}</p>
        </div>
        {copyable && (
          <Button
            variant='ghost'
            size='sm'
            onClick={() => handleCopy(value, fieldName)}
            className='shrink-0 opacity-0 group-hover:opacity-100 transition-opacity'
          >
            {copiedField === fieldName ? (
              <CheckCircle2 className='h-4 w-4 text-success' />
            ) : (
              <Copy className='h-4 w-4 text-muted-foreground' />
            )}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-4xl max-h-[90vh] flex flex-col p-0 min-h-0 !top-[4%] sm:!top-[50%] !translate-y-0 sm:!translate-y-[-50%]'>
        <AnimatePresence mode='wait'>
          {step === 'info' && (
            <motion.div
              key='info'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className='flex flex-col min-h-0'
            >
              <DialogHeader className='pt-4 sm:pt-6 px-6 pb-4'>
                <div className='flex items-start justify-between'>
                  <div>
                    <DialogTitle className='text-xl font-bold text-foreground mb-1'>
                      {isVietnamese ? 'Thanh toán bài thi' : 'Payment'}
                    </DialogTitle>
                    <DialogDescription className='text-sm text-muted-foreground'>
                      {isVietnamese ? examTitleVi : examTitle}
                    </DialogDescription>
                  </div>
                  <Badge className='bg-primary/10 text-primary  border-primary/40'>
                    <Shield className='h-3 w-3 mr-1' />
                    {isVietnamese ? 'Bảo mật' : 'Secure'}
                  </Badge>
                </div>
              </DialogHeader>

              <div className='flex-1 overflow-y-auto px-6 pb-28 min-h-0'>
                {isMockAllowed && (
                  <div className='mb-4 rounded-lg border border-warning/40 bg-warning/10 p-4'>
                    <p className='text-sm font-semibold text-warning'>
                      {isVietnamese
                        ? 'Chế độ giả lập đang bật  -  không có giao dịch thực tế.'
                        : 'Mock payment mode is active  -  no real transaction is created.'}
                    </p>
                    <p className='text-xs text-foreground mt-2'>
                      {isVietnamese
                        ? 'Nhấn nút "Đã thanh toán - Kiểm tra ngay" để hoàn tất và mở khóa bài thi.'
                        : 'Click "Payment Sent - Check Now" to finish and unlock the exam.'}
                    </p>
                  </div>
                )}

                {/* Access Code Input Section */}
                {!error && (
                  <div className='mb-4'>
                    <AccessCodeInput
                      onCodeApplied={codeData => {
                        setValidatedCode(codeData);
                        setPaymentAmount(codeData.finalAmount);

                        // Regenerate QR with access code - backend calculates discount
                        // Pass finalAmount to ensure we send 0 if it's free
                        createPaymentSession(codeData.code, codeData.finalAmount);
                      }}
                      onCodeRemoved={() => {
                        setValidatedCode(null);
                        setPaymentAmount(price);
                        // Regenerate QR without access code - backend uses original price
                        // Pass price explicitly to avoid race condition with setValidatedCode state
                        createPaymentSession(null, price);
                      }}
                      examId={examId}
                      assessmentId={assessmentId}
                      systemContext='THPT'
                      originalAmount={price}
                      disabled={isLoading}
                    />
                  </div>
                )}

                {/* Error State - Show prominently when API fails */}
                {error && !transactionId ? (
                  <div className='flex flex-col items-center justify-center py-12'>
                    <div className='mb-6 flex justify-center'>
                      <div className='rounded-full bg-red-100 dark:bg-red-900/30 p-6'>
                        <AlertCircle className='h-16 w-16 text-red-600 dark:text-red-400' />
                      </div>
                    </div>
                    <h3 className='text-2xl font-bold text-foreground mb-2'>
                      {isVietnamese ? 'Lỗi hệ thống' : 'System Error'}
                    </h3>
                    <p className='text-base text-muted-foreground mb-6 text-center max-w-md'>
                      {isVietnamese
                        ? 'Không thể tạo mã thanh toán. Vui lòng thử lại sau.'
                        : 'Unable to create payment code. Please try again later.'}
                    </p>
                    <div className='w-full max-w-md rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 mb-6'>
                      <div className='flex items-start gap-3'>
                        <AlertCircle className='h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5' />
                        <div className='flex-1'>
                          <p className='text-sm font-semibold text-red-800 dark:text-red-200 mb-1'>
                            {isVietnamese ? 'Chi tiết lỗi:' : 'Error Details:'}
                          </p>
                          <p className='text-sm text-red-700 dark:text-red-300 break-words'>
                            {error}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => createPaymentSession()}
                      disabled={isLoading}
                      className='bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white font-semibold shadow-lg shadow-primary/25 transition-all duration-300'
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                          {isVietnamese ? 'Đang thử lại...' : 'Retrying...'}
                        </>
                      ) : (
                        <>
                          {isVietnamese ? 'Thử lại' : 'Try Again'}
                          <ArrowRight className='ml-2 h-4 w-4' />
                        </>
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                    {/* Left Column - QR Code */}
                    <div className='space-y-3'>
                      <div className='rounded-xl bg-gradient-to-br from-primary/10 to-accent/20 backdrop-blur-sm p-4 border border-primary/50'>
                        <div className='text-center mb-3'>
                          <h3 className='text-base font-semibold text-foreground mb-1'>
                            {isVietnamese ? 'Quét mã QR để thanh toán' : 'Scan QR to Pay'}
                          </h3>
                          <p className='text-xs text-muted-foreground'>
                            {isVietnamese
                              ? 'Sử dụng app ngân hàng của bạn'
                              : 'Use your banking app'}
                          </p>
                        </div>

                        <div className='flex justify-center mb-4'>
                          {isLoading || (!qrData && !qrCodeImage) ? (
                            <div className='rounded-2xl bg-white p-10 shadow-xl border-2 border-primary/20 flex flex-col items-center justify-center gap-3'>
                              <Loader2 className='h-10 w-10 text-primary animate-spin' />
                              <p className='text-sm text-muted-foreground font-medium'>
                                {isVietnamese ? 'Đang tạo mã QR...' : 'Generating QR code...'}
                              </p>
                            </div>
                          ) : qrCodeImage ? (
                            // Display base64 image if provided by backend (VietQR)
                            <div className='rounded-2xl bg-white p-1 shadow-xl border-2 border-primary/20'>
                              <img
                                src={qrCodeImage}
                                alt='QR Code thanh toán'
                                className='w-[300px] h-[300px] object-contain'
                              />
                            </div>
                          ) : qrData ? (
                            // Generate QR code from data string
                            <div className='rounded-2xl bg-white p-1 shadow-xl border-2 border-primary/20'>
                              <QRCodeSVG
                                value={qrData}
                                size={300}
                                level='H'
                                includeMargin={false}
                                fgColor='#1a1a1a'
                                bgColor='#ffffff'
                              />
                            </div>
                          ) : null}
                        </div>

                        <div className='rounded-xl bg-gradient-to-r from-primary/5 to-accent/5 backdrop-blur-sm p-4 border border-primary/30'>
                          {discountAmount > 0 && (
                            <>
                              <div className='flex items-center justify-between mb-1'>
                                <span className='text-xs font-medium text-muted-foreground'>
                                  {isVietnamese ? 'Giá niêm yết' : 'Original Price'}
                                </span>
                                <span className='text-sm font-semibold text-foreground line-through opacity-70'>
                                  {new Intl.NumberFormat('vi-VN').format(originalAmount)}đ
                                </span>
                              </div>
                              <div className='flex items-center justify-between mb-1 text-success'>
                                <span className='text-xs font-medium'>
                                  {isVietnamese ? 'Giảm giá' : 'Discount'}
                                </span>
                                <span className='text-sm font-bold'>
                                  -{new Intl.NumberFormat('vi-VN').format(discountAmount)}đ
                                </span>
                              </div>
                              <div className='border-t border-primary/10 my-1 opacity-20'></div>
                            </>
                          )}
                          <div className='flex items-center justify-between mb-1'>
                            <span className='text-xs font-medium text-muted-foreground'>
                              {isVietnamese ? 'Giá chưa thuế' : 'Base Price'}
                            </span>
                            <span className='text-sm font-semibold text-foreground'>
                              {new Intl.NumberFormat('vi-VN').format(
                                Math.round(paymentAmount / 1.08)
                              )}
                              đ
                            </span>
                          </div>
                          <div className='flex items-center justify-between mb-3'>
                            <span className='text-xs font-medium text-muted-foreground'>
                              {isVietnamese ? 'Thuế GTGT (8%)' : 'VAT (8%)'}
                            </span>
                            <span className='text-sm font-semibold text-foreground'>
                              {new Intl.NumberFormat('vi-VN').format(
                                paymentAmount - Math.round(paymentAmount / 1.08)
                              )}
                              đ
                            </span>
                          </div>
                          <div className='border-t border-primary/20 my-2'></div>
                          <div className='flex items-center justify-between'>
                            <span className='text-sm font-bold text-muted-foreground uppercase tracking-wider'>
                              {isVietnamese ? 'Tổng thanh toán' : 'Total'}
                            </span>
                            <span className='text-2xl font-black text-orange-600 drop-shadow-sm'>
                              {formattedPrice}đ
                            </span>
                          </div>
                          <div className='flex items-center justify-between'>
                            <span className='text-xs text-muted-foreground'>
                              {isVietnamese ? 'Mã giao dịch' : 'Transaction ID'}
                            </span>
                            <div className='flex items-center gap-2'>
                              <code className='text-xs font-mono text-foreground bg-muted px-2 py-1 rounded'>
                                {transactionRef || (isVietnamese ? 'Đang tạo...' : 'Generating...')}
                              </code>
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => handleCopy(transactionRef, 'qr-ref')}
                                className='h-6 w-6 p-0'
                                disabled={!transactionRef}
                              >
                                {copiedField === 'qr-ref' ? (
                                  <CheckCircle2 className='h-3 w-3 text-success' />
                                ) : (
                                  <Copy className='h-3 w-3' />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='flex items-start gap-2 rounded-lg bg-info/10 border border-info/30 p-2'>
                        <Smartphone className='h-5 w-5 text-info shrink-0 mt-0.5' />
                        <p className='text-xs text-foreground leading-tight'>
                          {isVietnamese
                            ? 'Mở app ngân hàng → Chọn chức năng quét QR → Quét mã để thanh toán'
                            : 'Open banking app → Select QR scan → Scan to pay'}
                        </p>
                      </div>
                    </div>

                    {/* Right Column - Bank Details */}
                    <div className='space-y-4'>
                      <div>
                        <h3 className='text-base font-semibold text-foreground mb-2 flex items-center gap-2'>
                          <Building2 className='h-4 w-4 text-primary' />
                          {isVietnamese ? 'Hoặc chuyển khoản thủ công' : 'Or Manual Transfer'}
                        </h3>

                        <div className='space-y-2'>
                          <InfoField
                            icon={Building2}
                            label={isVietnamese ? 'Ngân hàng' : 'Bank'}
                            value={bankName || (isVietnamese ? 'Đang cập nhật' : 'Updating...')}
                            copyable={false}
                          />

                          {accountName && (
                            <InfoField
                              icon={User}
                              label={isVietnamese ? 'Chủ tài khoản' : 'Account Name'}
                              value={accountName}
                              copyable={Boolean(accountName)}
                              fieldName='name'
                            />
                          )}

                          <InfoField
                            icon={Hash}
                            label={isVietnamese ? 'Số tài khoản' : 'Account Number'}
                            value={accountNo || (isVietnamese ? 'Đang cập nhật' : 'Updating...')}
                            copyable={Boolean(accountNo)}
                            fieldName='account'
                          />

                          <div className='rounded-lg border-2 border-primary/40 bg-primary/10 p-3 text-foreground'>
                            <div className='space-y-1'>
                              {transactionId ? (
                                <div className='flex items-center justify-between mb-1'>
                                  <span className='text-xs font-medium opacity-70'>
                                    {isVietnamese ? 'Mã giao dịch' : 'Transaction ID'}
                                  </span>
                                  <code className='text-xs font-mono text-primary font-bold'>
                                    {transactionId}
                                  </code>
                                </div>
                              ) : null}

                              {discountAmount > 0 && (
                                <>
                                  <div className='flex items-center justify-between mb-1'>
                                    <span className='text-xs font-medium opacity-70'>
                                      {isVietnamese ? 'Giá gốc' : 'Original Price'}
                                    </span>
                                    <span className='text-xs font-semibold line-through opacity-60'>
                                      {new Intl.NumberFormat('vi-VN').format(originalAmount)}đ
                                    </span>
                                  </div>
                                  <div className='flex items-center justify-between mb-1 text-success'>
                                    <span className='text-xs font-medium'>
                                      {isVietnamese ? 'Giảm giá' : 'Discount'}
                                    </span>
                                    <span className='text-xs font-bold'>
                                      -{new Intl.NumberFormat('vi-VN').format(discountAmount)}đ
                                    </span>
                                  </div>
                                  <div className='border-t border-primary/10 my-1'></div>
                                </>
                              )}

                              <div className='flex items-center justify-between mb-1'>
                                <span className='text-xs font-medium opacity-70'>
                                  {isVietnamese ? 'Giá chưa thuế' : 'Base Price'}
                                </span>
                                <span className='text-xs font-semibold'>
                                  {new Intl.NumberFormat('vi-VN').format(
                                    Math.round(paymentAmount / 1.08)
                                  )}
                                  đ
                                </span>
                              </div>
                              <div className='flex items-center justify-between mb-1'>
                                <span className='text-xs font-medium opacity-70'>
                                  {isVietnamese ? 'Thuế GTGT (8%)' : 'VAT (8%)'}
                                </span>
                                <span className='text-xs font-semibold'>
                                  {new Intl.NumberFormat('vi-VN').format(
                                    paymentAmount - Math.round(paymentAmount / 1.08)
                                  )}
                                  đ
                                </span>
                              </div>
                              <div className='border-t border-primary/20 my-1'></div>
                              <div className='flex items-center justify-between pt-1'>
                                <span className='text-sm font-bold'>
                                  {isVietnamese ? 'Tổng thanh toán' : 'Total Amount'}
                                </span>
                                <span className='text-xl font-bold text-primary'>
                                  {formattedPrice}đ
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='space-y-1 rounded-lg bg-success/10 border border-success/30 p-3'>
                        <div className='flex items-center gap-2 text-xs text-foreground dark:text-foreground'>
                          <CheckCircle className='h-4 w-4 text-success' />
                          <span>{isVietnamese ? 'Xác thực tức thì' : 'Instant verification'}</span>
                        </div>
                        <div className='flex items-center gap-2 text-xs text-foreground dark:text-foreground'>
                          <CheckCircle className='h-4 w-4 text-success' />
                          <span>{isVietnamese ? 'Bảo mật 100%' : '100% secure'}</span>
                        </div>
                        <div className='flex items-center gap-2 text-xs text-foreground dark:text-foreground'>
                          <CheckCircle className='h-4 w-4 text-success' />
                          <span>{isVietnamese ? 'Hoàn tiền trong 7 ngày' : '7-day refund'}</span>
                        </div>
                      </div>

                      {/* Mobile QR Scan Tip */}
                      <div className='rounded-xl bg-gradient-to-br from-info/10 to-primary/10 border-2 border-info/30 p-4'>
                        <div className='flex items-start gap-3'>
                          <div className='flex-shrink-0 w-10 h-10 rounded-full bg-info/20 flex items-center justify-center'>
                            <Smartphone className='h-5 w-5 text-info' />
                          </div>
                          <div className='flex-1'>
                            <h4 className='text-sm font-semibold text-foreground mb-2'>
                              {isVietnamese ? '📱 Đang dùng điện thoại?' : '📱 On mobile device?'}
                            </h4>
                            <div className='space-y-2 text-xs text-foreground'>
                              <div className='flex items-start gap-2'>
                                <span className='flex-shrink-0 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold'>
                                  1
                                </span>
                                <span>
                                  {isVietnamese
                                    ? 'Chụp ảnh màn hình mã QR này'
                                    : 'Take a screenshot of this QR code'}
                                </span>
                              </div>
                              <div className='flex items-start gap-2'>
                                <span className='flex-shrink-0 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold'>
                                  2
                                </span>
                                <span>
                                  {isVietnamese
                                    ? 'Mở app ngân hàng → Quét QR → Chọn "Quét từ thư viện ảnh"'
                                    : 'Open banking app → Scan QR → Select "Scan from gallery"'}
                                </span>
                              </div>
                              <div className='flex items-start gap-2'>
                                <span className='flex-shrink-0 w-5 h-5 rounded-full bg-success text-white flex items-center justify-center text-xs font-bold'>
                                  ✓
                                </span>
                                <span className='font-medium text-success'>
                                  {isVietnamese
                                    ? 'Chọn ảnh vừa chụp và thanh toán!'
                                    : 'Select the screenshot and pay!'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* BIDV Promotional Banner - Show in payment step */}
                {step === 'info' && !error && (
                  <div className='mt-4'>
                    <BidvPromoBanner variant='full' />
                  </div>
                )}
              </div>

              {/* Error Message - Only show if there's a transaction ID (polling error) */}
              {error && transactionId && (
                <div className='mx-6 mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4'>
                  <div className='flex items-start gap-2'>
                    <AlertCircle className='h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5' />
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-red-800 dark:text-red-200'>{error}</p>
                      <Button
                        onClick={() => createPaymentSession()}
                        variant='link'
                        size='sm'
                        className='mt-2 p-0 h-auto text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300'
                      >
                        {isVietnamese ? 'Thử lại' : 'Retry'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Polling Status */}
              {isPolling && (
                <div className='mx-6 mb-4 rounded-lg bg-gradient-to-br from-emerald-50/80 to-teal-50/80 dark:from-emerald-900/30 dark:to-teal-900/30 backdrop-blur-sm border border-emerald-200/50 dark:border-emerald-800/50 p-4'>
                  <div className='flex items-center gap-3'>
                    <Loader2 className='h-5 w-5 text-emerald-600 dark:text-emerald-400 animate-spin' />
                    <div className='flex-1'>
                      <p className='text-sm font-medium text-gray-900 dark:text-white'>
                        {isVietnamese
                          ? 'Đang tự động kiểm tra thanh toán...'
                          : 'Automatically checking payment...'}
                      </p>
                      <p className='text-xs text-gray-600 dark:text-gray-400 mt-1'>
                        {isVietnamese
                          ? `Hệ thống đang tự động kiểm tra. Bạn có thể quét mã QR và thanh toán bất cứ lúc nào.`
                          : `System is automatically checking. You can scan the QR code and pay anytime.`}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Sticky Action Buttons - Only show when not in error state */}
              {!error || transactionId ? (
                <div className='absolute bottom-0 left-0 right-0 bg-card border-t border-border p-4'>
                  <div className='flex gap-3'>
                    <Button
                      variant='outline'
                      onClick={() => onOpenChange(false)}
                      disabled={isLoading || isPolling}
                      className='flex-1 border border-border hover:bg-muted hover:border-primary/30 transition-all duration-300 disabled:opacity-50'
                    >
                      {isVietnamese ? 'Hủy' : 'Cancel'}
                    </Button>
                    <Button
                      onClick={handleConfirmPayment}
                      disabled={isManualChecking || isLoading || !transactionId}
                      className='flex-1 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-white font-semibold shadow-lg shadow-primary/25 transition-all duration-300 disabled:opacity-50'
                    >
                      {isManualChecking ? (
                        <>
                          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                          {isVietnamese ? 'Đang kiểm tra...' : 'Checking...'}
                        </>
                      ) : isLoading ? (
                        <>
                          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                          {isVietnamese ? 'Đang tạo mã...' : 'Creating...'}
                        </>
                      ) : (
                        <>
                          {isVietnamese
                            ? 'Đã thanh toán - Kiểm tra ngay'
                            : 'Payment Sent - Check Now'}
                          <ArrowRight className='ml-2 h-4 w-4' />
                        </>
                      )}
                    </Button>
                  </div>
                  {!error && transactionId && (
                    <p className='text-xs text-center text-muted-foreground mt-2 leading-tight'>
                      {isVietnamese
                        ? '💡 Hệ thống đang tự động kiểm tra thanh toán mỗi 5 giây. Bạn có thể quét QR và thanh toán, hoặc nhấn nút "Đã thanh toán" để kiểm tra ngay.'
                        : '💡 System automatically checks payment every 5 seconds. You can scan QR and pay, or click "Payment Sent" to check immediately.'}
                    </p>
                  )}
                </div>
              ) : null}
            </motion.div>
          )}

          {step === 'processing' && (
            <motion.div
              key='processing'
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className='flex items-center justify-center p-12'
            >
              <div className='text-center'>
                <div className='mb-6 flex justify-center'>
                  <div className='rounded-full bg-primary/10 p-6'>
                    <Loader2 className='h-16 w-16 animate-spin text-primary' />
                  </div>
                </div>
                <h3 className='text-2xl font-bold text-foreground mb-2'>
                  {isVietnamese ? 'Đang xác thực thanh toán...' : 'Verifying Payment...'}
                </h3>
                <p className='text-muted-foreground mb-6'>
                  {isVietnamese ? 'Vui lòng đợi trong giây lát' : 'Please wait a moment'}
                </p>
                <div className='flex items-center justify-center gap-2'>
                  <Clock className='h-4 w-4 text-muted-foreground' />
                  <span className='text-sm text-muted-foreground'>
                    {isVietnamese ? 'Thời gian xử lý: 2-3 giây' : 'Processing time: 2-3 seconds'}
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {step === 'free' && (
            <motion.div
              key='free'
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className='flex items-center justify-center p-12'
            >
              <div className='text-center'>
                <div className='mb-6 flex justify-center'>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className='rounded-full bg-gradient-to-br from-success/20 to-primary/20 p-6'
                  >
                    <CheckCircle className='h-16 w-16 text-success' />
                  </motion.div>
                </div>
                <h3 className='text-2xl font-bold text-foreground mb-2'>
                  {isVietnamese ? '🎉 Miễn phí toàn bộ!' : '🎉 100% Free Access!'}
                </h3>
                <p className='text-muted-foreground mb-4'>
                  {isVietnamese
                    ? 'Mã giảm giá của bạn đã được áp dụng thành công'
                    : 'Your discount code has been applied successfully'}
                </p>
                <div className='rounded-lg bg-success/10 border border-success/30 p-4 mb-6'>
                  <p className='text-sm font-semibold text-success mb-1'>
                    {isVietnamese ? 'Bạn không cần thanh toán!' : 'No payment required!'}
                  </p>
                  <p className='text-sm text-foreground'>
                    {isVietnamese ? examTitleVi : examTitle}
                  </p>
                </div>
                <Button
                  onClick={() => {
                    // Pass undefined for free payments - parent should skip link-payment call
                    onPaymentSuccess(undefined);
                    onOpenChange(false);
                  }}
                  className='w-full bg-gradient-to-r from-success to-primary hover:from-success/90 hover:to-primary/90 text-white font-semibold shadow-lg transition-all duration-300'
                >
                  {isVietnamese ? 'Bắt đầu làm bài thi' : 'Start Exam'}
                  <ArrowRight className='ml-2 h-4 w-4' />
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'success' && (
            <motion.div
              key='success'
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className='flex items-center justify-center p-12'
            >
              <div className='text-center'>
                <div className='mb-6 flex justify-center'>
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className='rounded-full bg-primary/10 p-6'
                  >
                    <CheckCircle className='h-16 w-16 text-primary' />
                  </motion.div>
                </div>
                <h3 className='text-2xl font-bold text-foreground mb-2'>
                  {isVietnamese ? 'Thanh toán thành công!' : 'Payment Successful!'}
                </h3>
                <p className='text-muted-foreground mb-6'>
                  {isVietnamese
                    ? 'Bạn đã có quyền truy cập vào bài thi'
                    : 'You now have access to the exam'}
                </p>
                <div className='rounded-lg bg-muted dark:bg-muted/50 border border-border p-4 mb-6'>
                  <p className='text-sm text-foreground dark:text-foreground'>
                    {isVietnamese ? examTitleVi : examTitle}
                  </p>
                </div>
                <p className='text-sm text-muted-foreground'>
                  {isVietnamese
                    ? `Tự động chuyển hướng trong ${countdown} giây...`
                    : `Redirecting in ${countdown} seconds...`}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
